import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import { Warning as WarningIcon, ExpandMore, ExpandLess } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../api";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  tableWrapper: {
    flex: 1,
    minWidth: 0,
  },
  countCell: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  summaryRow: {
    "& td": {
      fontWeight: 700,
      borderTop: `2px solid ${theme.palette.divider}`,
    },
  },
  warningRow: {
    backgroundColor: "#fff8e1",
  },
  activeBadge: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    fontSize: 12,
  },
  inactiveBadge: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    fontWeight: 600,
    fontSize: 12,
  },
  healthChecks: {
    marginTop: theme.spacing(3),
  },
  healthCheckCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    backgroundColor: "#fff8e1",
    border: "1px solid #ffe082",
    borderRadius: 4,
  },
  healthCheckClickable: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#fff3cd",
    },
  },
  drilldownTable: {
    marginBottom: theme.spacing(1),
  },
  customerLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  healthCheckIcon: {
    color: "#f9a825",
    fontSize: 20,
  },
  healthCheckCount: {
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    marginLeft: "auto",
  },
}));

const ACTIVE_CASE_STATUSES = ["submitted", "pending_opt_in", "pending_opt_out"];

function isAnomalous(row) {
  if (row.ecpActive && !ACTIVE_CASE_STATUSES.includes(row.caseStatus)) return true;
  if (row.ecpActive && row.caseStatus === "closed") return true;
  if (row.raeStatus === null && row.caseStatus === "submitted") return true;
  if (row.ecpActive && !row.pcActive) return true;
  return false;
}

function formatCount(n) {
  return n.toLocaleString();
}

function StatusTable({ title, rows }) {
  const classes = useStyles();
  const total = rows.reduce((sum, r) => sum + r.count, 0);

  return (
    <Box className={classes.tableWrapper}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ECP Active</TableCell>
              <TableCell>Case Status</TableCell>
              <TableCell>RAE Status</TableCell>
              <TableCell>PC Active</TableCell>
              <TableCell align="right">Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i} className={isAnomalous(row) ? classes.warningRow : undefined}>
                <TableCell>
                  <Chip
                    label={row.ecpActive ? "Active" : "Inactive"}
                    size="small"
                    className={row.ecpActive ? classes.activeBadge : classes.inactiveBadge}
                  />
                </TableCell>
                <TableCell>{row.caseStatus}</TableCell>
                <TableCell>{row.raeStatus ?? "None"}</TableCell>
                <TableCell>
                  <Chip
                    label={row.pcActive ? "Active" : "Inactive"}
                    size="small"
                    className={row.pcActive ? classes.activeBadge : classes.inactiveBadge}
                  />
                </TableCell>
                <TableCell className={classes.countCell}>{formatCount(row.count)}</TableCell>
              </TableRow>
            ))}
            <TableRow className={classes.summaryRow}>
              <TableCell colSpan={4}>Total</TableCell>
              <TableCell className={classes.countCell}>{formatCount(total)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function MultipleRaesDrilldown({ classes }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .getMultipleActiveRaes()
      .then(api.pickData)
      .then((d) => {
        setRows(d || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <Typography variant="body2" style={{ padding: 8 }}>
        No customers found.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" className={classes.drilldownTable}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Customer ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell align="right">Active RAE Count</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.slice(0, 10).map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <Link to={`/customers/${row.id}`} className={classes.customerLink}>
                  {row.id}
                </Link>
              </TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell align="right">{row.activeRaeCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function EnrollmentStatsPanel() {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [raesExpanded, setRaesExpanded] = useState(false);

  useEffect(() => {
    api
      .getEnrollmentCasePartnerStats()
      .then(api.pickData)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || "Failed to load enrollment stats");
        setLoading(false);
      });
  }, []);

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!data) return <Typography>No data available.</Typography>;

  const healthChecks = data.healthChecks;

  return (
    <>
      {healthChecks && (
        <Box className={classes.healthChecks}>
          <Typography variant="h6" gutterBottom>
            Health Checks
          </Typography>
          <Box
            className={`${classes.healthCheckCard} ${classes.healthCheckClickable}`}
            onClick={() => setRaesExpanded((v) => !v)}
          >
            <WarningIcon className={classes.healthCheckIcon} />
            <Typography variant="body2">
              Customers with multiple active RAEs
            </Typography>
            <Typography variant="body2" className={classes.healthCheckCount}>
              {formatCount(healthChecks.customersWithMultipleActiveRaes ?? 0)}
            </Typography>
            {raesExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </Box>
          {raesExpanded && <MultipleRaesDrilldown classes={classes} />}
          <Box className={classes.healthCheckCard}>
            <WarningIcon className={classes.healthCheckIcon} />
            <Typography variant="body2">
              Active PCs with only terminal enrollments
            </Typography>
            <Typography variant="body2" className={classes.healthCheckCount}>
              {formatCount(healthChecks.activePcsWithOnlyTerminalEnrollments ?? 0)}
            </Typography>
          </Box>
        </Box>
      )}
      <Box className={classes.container} mt={3}>
        <StatusTable title="SDK Partners" rows={data.sdk || []} />
        <StatusTable title="Non-SDK Partners" rows={data.nonSdk || []} />
      </Box>
    </>
  );
}
